import { MemberRole } from '../../types/organisation';
import { hasRole } from '../../utilities/roleUtils';

/**
 * Pure helpers for the `Organisations` map stored on user documents.
 *
 * Extracted from UserManager so the role/membership rules can be unit tested
 * without Firestore. All functions are side-effect free and never mutate
 * their inputs.
 */

export interface UserOrganisationEntry {
  roles?: MemberRole[];
  /** Legacy single-role format, predating the roles array */
  role?: MemberRole;
  email?: string;
  facilitatorInfo?: FacilitatorInfoData;
  [key: string]: unknown;
}

export type OrganisationRoleLookup = Record<
  string,
  { roles?: MemberRole[]; role?: MemberRole } | undefined
>;

export type UserOrganisations = Record<string, UserOrganisationEntry>;

export interface FacilitatorInfoData {
  contractSigned?: boolean;
  dbsNumberShared?: boolean;
  contractSignedDate?: unknown;
  dbsNumberSharedDate?: unknown;
  contractNotes?: string;
  dbsNotes?: string;
}

export interface FacilitatorInfo {
  contractSigned: boolean;
  dbsNumberShared: boolean;
  contractSignedDate?: unknown;
  dbsNumberSharedDate?: unknown;
  contractNotes?: string;
  dbsNotes?: string;
}

export interface FacilitatorProfileCompleteness {
  isComplete: boolean;
  email: string;
  name: string;
  userExists: boolean;
}

interface UserDocumentData {
  email?: string;
  Organisations?: UserOrganisations;
  facilitatorInfo?: FacilitatorInfoData;
  userProfile?: { userName?: string };
}

/**
 * Roles on an organisation entry, including the legacy single-`role` field.
 */
export const getEntryRoles = (entry: UserOrganisationEntry | undefined): MemberRole[] => {
  if (!entry) return [];
  if (Array.isArray(entry.roles) && entry.roles.length > 0) return [...entry.roles];
  if (entry.role) return [entry.role];
  return [];
};

/**
 * Adds a role to one organisation entry. Existing fields (facilitatorInfo, etc.)
 * are preserved. If the org is missing, `createEntry` is used as the base.
 * Returns the same map reference when nothing changes.
 */
export const addRoleToOrganisation = (
  organisations: UserOrganisations,
  orgId: string,
  role: MemberRole,
  createEntry?: UserOrganisationEntry
): UserOrganisations => {
  const existing = organisations[orgId];
  if (!existing) {
    return {
      ...organisations,
      [orgId]: { ...(createEntry || {}), roles: [role] },
    };
  }
  const existingRoles = getEntryRoles(existing);
  if (existingRoles.includes(role)) {
    return organisations;
  }
  return {
    ...organisations,
    [orgId]: { ...existing, roles: [...existingRoles, role] },
  };
};

/**
 * Removes a role from one organisation entry. The org is dropped if no roles remain.
 * Returns the same map reference when nothing changes.
 */
export const removeRoleFromOrganisation = (
  organisations: UserOrganisations,
  orgId: string,
  role: MemberRole
): UserOrganisations => {
  const existing = organisations[orgId];
  const existingRoles = getEntryRoles(existing);
  if (!existing || !existingRoles.includes(role)) {
    return organisations;
  }
  const updatedRoles = existingRoles.filter((item) => item !== role);
  const updated = { ...organisations };
  if (updatedRoles.length === 0) {
    delete updated[orgId];
  } else {
    updated[orgId] = { ...existing, roles: updatedRoles };
  }
  return updated;
};

/**
 * Organisation ids where the user holds the given role (roles array only).
 */
export const getOrgIdsWithRole = (
  organisations: OrganisationRoleLookup,
  role: MemberRole
): string[] =>
  Object.keys(organisations).filter(orgId => hasRole(organisations[orgId]?.roles, role));

/**
 * Organisation ids where the user holds the given role, also accepting the
 * legacy single-`role` format.
 */
export const getOrgIdsWithRoleIncludingLegacy = (
  organisations: OrganisationRoleLookup,
  role: MemberRole
): string[] =>
  Object.keys(organisations).filter(orgId => {
    const entry = organisations[orgId];
    const roles = entry?.roles || (entry?.role ? [entry.role] : []);
    return roles.includes(role);
  });

/**
 * Whether the user holds the given role in any organisation.
 */
export const hasRoleInAnyOrganisation = (
  organisations: UserOrganisations,
  role: MemberRole
): boolean => Object.values(organisations).some(org => hasRole(org?.roles, role));

/**
 * Returns a copy of the organisations map with every entry holding the given
 * role removed entirely.
 */
export const removeOrganisationsWithRole = (
  organisations: UserOrganisations,
  role: MemberRole
): UserOrganisations => {
  const updated = { ...organisations };
  Object.keys(updated).forEach(orgId => {
    if (hasRole(updated[orgId]?.roles, role)) {
      delete updated[orgId];
    }
  });
  return updated;
};

/**
 * Returns a copy of the organisations map with the given role removed from
 * each entry's roles array. Entries left with no roles are removed entirely.
 */
export const removeRoleFromOrganisations = (
  organisations: UserOrganisations,
  role: MemberRole
): UserOrganisations => {
  const updated = { ...organisations };
  Object.keys(updated).forEach(orgId => {
    const org = updated[orgId];
    if (org && hasRole(org.roles, role)) {
      const updatedRoles = (org.roles as MemberRole[]).filter(r => r !== role);
      if (updatedRoles.length === 0) {
        delete updated[orgId];
      } else {
        updated[orgId] = { ...org, roles: updatedRoles };
      }
    }
  });
  return updated;
};

/**
 * Resolves facilitator compliance info from a user document.
 * Prefers Organisations[orgId].facilitatorInfo of the first facilitator
 * organisation (legacy role format included), falling back to the root
 * facilitatorInfo field.
 */
export const resolveFacilitatorInfo = (userData: UserDocumentData): FacilitatorInfo => {
  const organisations = userData.Organisations || {};
  const facilitatorOrgIds = getOrgIdsWithRoleIncludingLegacy(organisations, 'facilitator');

  let facilitatorInfoData: FacilitatorInfoData = {};
  if (facilitatorOrgIds.length > 0) {
    const firstFacilitatorOrg = organisations[facilitatorOrgIds[0]];
    facilitatorInfoData = firstFacilitatorOrg?.facilitatorInfo || userData.facilitatorInfo || {};
  } else {
    facilitatorInfoData = userData.facilitatorInfo || {};
  }

  return {
    contractSigned: facilitatorInfoData.contractSigned || false,
    dbsNumberShared: facilitatorInfoData.dbsNumberShared || false,
    contractSignedDate: facilitatorInfoData.contractSignedDate,
    dbsNumberSharedDate: facilitatorInfoData.dbsNumberSharedDate,
    contractNotes: facilitatorInfoData.contractNotes || '',
    dbsNotes: facilitatorInfoData.dbsNotes || ''
  };
};

/**
 * Resolves facilitator profile completeness from a user document.
 * The email comes from the first facilitator organisation's (encrypted)
 * email field, falling back to the top-level email.
 */
export const resolveFacilitatorProfileCompleteness = (
  userData: UserDocumentData
): FacilitatorProfileCompleteness => {
  const organisations = userData.Organisations || {};
  const facilitatorOrgIds = getOrgIdsWithRole(organisations, 'facilitator');

  let email = '';
  if (facilitatorOrgIds.length > 0) {
    email = organisations[facilitatorOrgIds[0]]?.email || '';
  }
  if (!email) {
    email = userData.email || '';
  }

  const name = userData.userProfile?.userName || '';

  const hasValidEmail = !!email && email.trim() !== '' && email !== 'No email';
  const hasValidName = !!name && name.trim() !== '';

  return {
    isComplete: hasValidEmail && hasValidName,
    email,
    name,
    userExists: true
  };
};
